import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ContractDetailsTypes } from "./types";

import DataField from "../../components/DataField/DataField";
import Header from "../../components/Header/Header";
import Checkbox from "../../components/Checkbox/Checkbox";

import {
  contractDetailsLabelKeysMapping as labelKeyMapping,
  termsAndConditions,
} from "../constants";

import "./styles.scss";
import { ContractContext } from "../../context/contract";
import { AuthContext } from "../../context/auth";
import NavigationButtons from "../../components/NavigationButtons/NavigationButtons";
import PDFViewer from "../../components/PDFViewer/PDFViewer";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";

const ContractDetailsPage: FC = () => {
  const {
    loading,
    contractDetails,
    getContractDetails,
    handleFormData,
    formData,
    isAtBottom,
    handleDisabledCheckbox,
    pdfLink,
  } = useContext(ContractContext);
  const { prospectId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [error, setError] = useState(false);

  const scrollableList = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!prospectId) return;
    getContractDetails(prospectId);
  }, [prospectId]);

  const handleScroll = () => {
    const listElement = scrollableList.current;
    if (listElement) {
      const scrollTop = listElement.scrollTop;
      const scrollHeight = listElement.scrollHeight;
      const clientHeight = listElement.clientHeight;
      const isScrolledToBottom =
        Math.ceil(scrollTop + clientHeight) === scrollHeight;
      handleDisabledCheckbox(isScrolledToBottom);
    }
  };

  const handleNextButtonClick = () => {
    if (formData.contractDetailsTnC) {
      navigate("/digital-contract/contract/customer-signature");
      return;
    }
    setError(true);
  };

  if (loading) return <FullPageLoader />;

  return pdfLink ? (
    <PDFViewer pdfLink={pdfLink} />
  ) : (
    <div className="primary-container">
      <Header />
      <div
        className="content-container"
        ref={scrollableList}
        onScroll={() => isAtBottom === false && handleScroll()}
      >
        <div className="customer-details">
          {Object.keys(contractDetails).map((key: string) => {
            return (
              <DataField
                label={
                  labelKeyMapping[key as keyof ContractDetailsTypes] ?? key
                }
                value={contractDetails[key as keyof ContractDetailsTypes]}
                className={"strong"}
              />
            );
          })}
        </div>

        <div className="terms-condition-container">
          <h4>TERMS & CONDITIONS</h4>
          <ol className="overflow-class">
            {termsAndConditions.map((term) => {
              return (
                <>
                  <li>{term.content}</li>
                  {term.subList && (
                    <ol type="a">
                      {term.subList.map((subTerm) => (
                        <li>{subTerm.content}</li>
                      ))}
                    </ol>
                  )}
                </>
              );
            })}
          </ol>
        </div>
      </div>
      <Checkbox
        title="I have read & agree with above T&Cs"
        disabled={!isAtBottom}
        defaultValue={formData.contractDetailsTnC}
        handleCheckboxChange={(value: boolean) => {
          if (value) setError(false);
          else setError(true);
          handleFormData({
            ...formData,
            contractDetailsTnC: value,
          });
        }}
        {...(error && {
          error: "This field is required.",
        })}
      />
      <NavigationButtons handleNextButtonClick={handleNextButtonClick} />
    </div>
  );
};

export default ContractDetailsPage;
